import { getConfig, URLS } from '@constants';

import { EAjaxFunctions, EApiUpstream, EContext } from '@enums';

import { getCgiUrl, Log } from '@utils';

import { ApiAppsFetchResponseType, ApiRateResponseType, IFetchAppsResponse } from '@api';

import { BaseApi } from '../BaseApi';
import {
	genresApps,
	installedApps,
	mainMenuMock,
	newApps,
	salesApps,
	showApp,
	showcaseApps,
	suggestionApps,
} from './mocks';

import {
	IApiBaseRequestParams,
	IAppRequestResponse,
	IAppsSalesResponse,
	ICGIUrlOptions,
	IMainMenuAppsResponse,
	IShowcaseResponse,
	IUser,
} from '@interfaces';

export class AppsApi extends BaseApi {

	async uninstallApp(appId: string, user?: IUser, isMobile?: boolean) {
		const url = getCgiUrl({
			url: URLS.AJAX,
			args: {
				appid: appId,
				context: isMobile && 'mobile',
			},
			params: {
				'func_name': EAjaxFunctions.APPS_UNINSTALL,
				...this.getUserParams(user),
			},
		});

		try {
			await this.get({
				url,
				upstream: EApiUpstream.MPOP,
			});
		} catch (err) {
			Log.error(`Couldn't uninstall app: ${err}`);
		}
	}

	async rateApp<T extends ApiRateResponseType>(appId: string, rating: number, user?: IUser, isMobile?: boolean) {
		const userParams = user ? this.getUserParams(user) : null;
		const url = getCgiUrl({
			url: URLS.AJAX,
			args: { context: isMobile && 'mobile' },
			params: {
				'func_name': EAjaxFunctions.APP_RATE,
				'xemail': userParams.xemail || undefined,
			},
		});

		try {
			const resp = await this.post<IApiBaseRequestParams, T>({
				url,
				upstream: EApiUpstream.MPOP,
				data: {
					mna: userParams.mna || undefined,
					mnb: userParams.mnb || undefined,
					data: JSON.stringify([false, false, appId, rating, '']),
				},
			});

			if (resp.data[2] !== 1) {
				Log.error(`Couldn't rate the app`);
			}

			return resp;
		} catch (err) {
			Log.error(`Couldn't rate the app: ${err}`);
		}
	}

	async fetchApps(func: EAjaxFunctions, options: ICGIUrlOptions): Promise<IFetchAppsResponse> {
		const limit = options?.limit ? Number(options.limit + 1) : 21;
		const response = await this.fetchCollection(func, { ...options, limit });
		const hasMore = response?.data?.length - options.limit > 0;
		const data = (response && Array.isArray(response?.data) && options.limit)
			? [...response?.data]?.splice(0, options.limit)
			: { ...response?.data };

		if (!response) {
			return null;
		}

		return {
			...response,
			data,
			hasMore,
		};
	}

	async fetchSales(func: EAjaxFunctions, options: ICGIUrlOptions): Promise<IAppsSalesResponse> {
		const response = await this.fetchCollection(func, options);

		return {
			data: response.data,
			unread: response.unread,
			offset: response.offset,
			total: response.total,
			limit: response.limit,
		};
	}

	async fetchShowcase(func: EAjaxFunctions, options: ICGIUrlOptions): Promise<IShowcaseResponse[]> {
		return await this.fetchCollection(func, options) as IShowcaseResponse[];
	}

	async fetchCollection<T extends ApiAppsFetchResponseType>(
		func: EAjaxFunctions,
		{
			offset = 0,
			limit = 20,
			filed = 2,
			target,
			genre,
			context,
			appId,
			user,
		}: ICGIUrlOptions,
		// eslint-disable-next-line @typescript-eslint/no-explicit-any
	): Promise<{ [key: string]: any }> {
		const url = getCgiUrl({
			url: URLS.AJAX,
			args: {
				offset,
				limit,
				filed,
				genre,
				checkPics: 1,
				target: target || (context === EContext.MOBILE && EContext.MOBILE) || '',
				context,
				appId,
			},
			params: {
				'func_name': func,
				...this.getUserParams(user),
			},
		});

		if (!!+getConfig().isMockData) {
			return this.getFakeCollection(func);
		}

		try {
			const resp = await this.get<IApiBaseRequestParams, T>({
				url,
				upstream: EApiUpstream.MPOP,
			});
			const [responseType, status, expandStatus, data] = resp?.data || [];

			if (expandStatus === 'ok' && data) {
				return data;
			} else {
				if (status === 'Auth') {
					throw new Error(`Auth error! ${responseType} - ${status} - ${expandStatus} - ${data}`);
				}
			}
		} catch (err) {
			throw err;
		}

		return null;
	}

	getFakeCollection<T extends ApiAppsFetchResponseType>(
		func: EAjaxFunctions,
		// eslint-disable-next-line @typescript-eslint/no-explicit-any
	): { [key: string]: any } {
		switch (func) {
			case EAjaxFunctions.APPS_INSTALLED:
				return installedApps[3];
			case EAjaxFunctions.APPS_NEW:
				return newApps[3];
			case EAjaxFunctions.APPS_POPULAR:
				return suggestionApps[3];
			case EAjaxFunctions.APPS_SALES:
				return salesApps[3];
			case EAjaxFunctions.APPS_SHOWCASE:
				return showcaseApps[3];
			case EAjaxFunctions.APPS_SHOW_BY_ID:
				return showApp[3];
			case EAjaxFunctions.APPS_GENRE:
				return genresApps[3];
		}
	}

	getUserParams(user: IUser) {
		return {
			xemail: user?.email || undefined,
			mna: user?.magicNumber?.a || undefined,
			mnb: user?.magicNumber?.b || undefined,
		};
	}

	// на опенстаке без почты отдаёт ошибку
	async fetchMainMenuApps<T extends IMainMenuAppsResponse>(user?: IUser) {
		const url = getCgiUrl({
			url: URLS.CATALOG_XML,
			params: {
				handler: 'user.left_menu',
				email: user?.email || undefined,
				format: 'json',
				res: 'json',
			},
		});

		try {
			let responseData;

			if (!!+getConfig().isMockData) {
				responseData = mainMenuMock as unknown as IMainMenuAppsResponse || null;
			} else {
				const resp = await this.get<IApiBaseRequestParams, T>({
					url,
					upstream: EApiUpstream.MPOP,
				});
				responseData = resp?.data || null;
			}

			return responseData;
		} catch (err) {
			Log.error(`Failed in getting apps in left menu by url: ${url};`);
		}
	}

	async fetchApp(appId: string) {
		try {
			const resp = await this.get<IApiBaseRequestParams, IAppRequestResponse>({
				url: URLS.MOBILE_REDIRECT,
				// eslint-disable-next-line @typescript-eslint/camelcase
				params: { app_id: appId },
				upstream: EApiUpstream.MPOP,
				headers: { 'X-Json-Res': 1 },
			});
			return resp.data || null;
		} catch (err) {
			throw err;
		}
	}

}
